import { useMutation, useQuery } from '@apollo/client/react';
import {
  GET_SHARE_LINK_BY_OBJECT_ID,
  CREATE_SHARE_LINK,
  UPDATE_SHARE_LINK,
  SET_SHARE_LINK_PASSWORD,
  REVEAL_SHARE_LINK_PASSWORD,
  ADD_SHARE_LINK_EMAILS,
  UPDATE_SHARE_LINK_PEOPLE_ROLE,
  DELETE_SHARE_LINK,
} from '@scio3/graphql-client';
import type { ShareLink } from '@scio3/shared-types';
import { toast } from 'sonner';
import i18next from 'i18next';

interface ShareLinkByObjectIdData {
  shareLinkByObjectId: ShareLink | null;
}

interface CreateShareLinkData {
  createShareLink: ShareLink;
}

interface RevealPasswordData {
  revealShareLinkPassword: { password?: string; message?: string };
}

export function useShareLink(objectId: string | undefined) {
  const { data, loading, refetch } = useQuery<ShareLinkByObjectIdData>(
    GET_SHARE_LINK_BY_OBJECT_ID,
    {
      variables: { objectId: objectId ?? '' },
      skip: !objectId,
      fetchPolicy: 'network-only',
    },
  );

  const [createShareLinkMutation, { loading: creating }] =
    useMutation<CreateShareLinkData>(CREATE_SHARE_LINK);
  const [updateShareLinkMutation, { loading: updating }] = useMutation(UPDATE_SHARE_LINK);
  const [setPasswordMutation] = useMutation(SET_SHARE_LINK_PASSWORD);
  const [revealPasswordMutation] = useMutation<RevealPasswordData>(REVEAL_SHARE_LINK_PASSWORD);
  const [addEmailsMutation, { loading: addingEmails }] = useMutation(ADD_SHARE_LINK_EMAILS);
  const [updatePeopleRoleMutation, { loading: updatingPeople }] = useMutation(
    UPDATE_SHARE_LINK_PEOPLE_ROLE,
  );
  const [deleteShareLinkMutation, { loading: deleting }] = useMutation(DELETE_SHARE_LINK);

  const shareLink = data?.shareLinkByObjectId ?? null;

  return {
    shareLink,
    loading,
    creating,
    updating,
    updatingPeople,
    addingEmails,
    deleting,
    refetch,

    createShareLink: async (payload: {
      objectId: string;
      type: string;
      subType?: string;
      accessType?: string;
      accessRole?: string;
    }) => {
      const result = await createShareLinkMutation({ variables: { payload } });
      return result.data?.createShareLink;
    },

    updateShareLink: async (
      _id: string,
      payload: {
        accessType?: string;
        accessRole?: string;
        passwordEnabled?: boolean;
        expirationEnabled?: boolean;
        expirationValue?: Date;
      },
    ) => {
      await updateShareLinkMutation({ variables: { _id, payload } });
      await refetch();
      toast.success(i18next.t('Settings updated'));
    },

    setPassword: async (_id: string, value: string) => {
      await setPasswordMutation({ variables: { _id, value } });
      await refetch();
      toast.success(i18next.t('Password updated'));
    },

    revealPassword: async (_id: string) => {
      const result = await revealPasswordMutation({ variables: { _id } });
      return result.data?.revealShareLinkPassword;
    },

    addEmails: async (
      _id: string,
      payload: {
        emails: string[];
        role: string;
        notifyEmail?: boolean;
        bccMyself?: boolean;
        notifyMessage?: string;
        objectName?: string;
        // reCAPTCHA — the backend rejects an empty token wherever reCaptchaSecret is
        // configured. See ShareLinkEmailPayload in open-api for why an authenticated
        // mutation carries one (volume control against scripted bulk sends).
        captcha?: string;
        captchaVersion?: 'v3' | 'v2';
        captchaAction?: string;
      },
    ) => {
      await addEmailsMutation({ variables: { _id, payload } });
      await refetch();
    },

    updatePeopleRole: async (_id: string, userId: string, role: string) => {
      await updatePeopleRoleMutation({ variables: { _id, userId, role } });
      await refetch();
      toast.success(i18next.t(role === 'removeAccess' ? 'Access removed' : 'Role updated'));
    },

    deleteShareLink: async (_id: string) => {
      await deleteShareLinkMutation({ variables: { _id } });
    },
  };
}
